import { useEffect, useState } from 'react';
import { ChatInterface, ChatSidebar } from '../components/organisms';
import { useProfile } from '../hooks/auth';
import { useCoachChat } from '../hooks/useCoachChat';
import AppHeader from '../components/organisms/AppHeader';
import Button from '../components/atoms/Button';
import { useAtom, useAtomValue } from 'jotai';
import { 
  activeThreadIdAtom, 
  createNewThreadAtom, 
  removeThreadAtom, 
  threadListAtom,
  threadMetadataAtom
} from '../store/chat-atoms';

// Thread session interface that matches ChatSession structure for sidebar compatibility
interface ThreadSession {
  id: string;
  title: string;
  userId?: string;
  threadId?: string;
  createdAt?: Date;
  updatedAt: Date;
  messages: { length: number }; // For compatibility with existing sidebar
}

export function ChatPage() {
  // Use TanStack Query hooks
  const { data: user } = useProfile();
  
  // Jotai state management
  const [activeThreadId] = useAtom(activeThreadIdAtom);
  const threadList = useAtomValue(threadListAtom);
  const threadMetadata = useAtomValue(threadMetadataAtom);
  const createNewThread = useAtom(createNewThreadAtom)[1];
  const removeThread = useAtom(removeThreadAtom)[1];
  
  // Coach chat hook for current thread
  const {
    messages,
    isLoading,
    error,
    streamingMessage,
    currentThreadId,
    scrollPosition,
    sendMessage,
    loadHistory,
    clearMessages,
    switchThread,
    saveScrollPosition,
  } = useCoachChat(activeThreadId ?? undefined);
  
  // Local state for UI
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [fetchedSessions, setFetchedSessions] = useState<Set<string>>(new Set());
  const [showToolBlocks, setShowToolBlocks] = useState(true);
  
  // Create thread sessions from thread metadata
  const threadSessions: ThreadSession[] = threadList.map(threadId => {
    const metadata = threadMetadata[threadId] ?? {
      id: threadId,
      title: `Thread ${threadId.slice(-8)}`,
      messageCount: 0,
      lastActivity: new Date()
    };
    
    return {
      id: threadId,
      title: metadata.title,
      userId: user?.id ?? '',
      threadId,
      createdAt: metadata.lastActivity,
      updatedAt: metadata.lastActivity,
      messages: { length: metadata.messageCount }
    };
  }).sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

  // Load backend history for active thread
  useEffect(() => {
    if (user?.id && currentThreadId) {
      fetchChatHistoryFromBackend(currentThreadId);
    }
  }, [user?.id, currentThreadId]);

  // getCurrentUser is now handled by useProfile hook

  const fetchChatHistoryFromBackend = async (threadId: string, force = false) => {
    // Check if we've already fetched history for this session
    if (!force && fetchedSessions.has(threadId)) {
      console.log(`History already fetched for thread ${threadId}, skipping`);
      return;
    }

    setIsLoadingHistory(true);
    try {
      // Get backend history using the threadId
      // Note: This could be converted to use useChatHistory hook with manual trigger
      const { CoachService } = await import('../services/coach');
      const backendHistory = await CoachService.getChatHistory(threadId);
      console.log(`Backend chat history for thread ${threadId}:`, backendHistory);
      
      if (backendHistory && backendHistory.length > 0) {
        // Convert backend history to local chat format
        const convertedMessages: any[] = backendHistory.map((item: any, index: number) => {
          const baseMessage = {
            id: `${item.role}-${Date.now()}-${index}`,
            role: item.role as 'user' | 'assistant' | 'tool-use' | 'tool-result',
            timestamp: new Date(Date.now() - (backendHistory.length - index) * 60000), // Stagger timestamps
          };

          // Handle internal messages (tool-use and tool-result)
          if (item.internal) {
            return {
              ...baseMessage,
              internal: true,
              content: item.content,
            };
          }

          // Handle regular messages
          return {
            ...baseMessage,
            widgets: item.inChatWidgets ?? [{ type: 'text', text: 'No content' }],
          };
        });

        // Update the thread state directly via Jotai
        // Note: This will be handled by the loadHistory function in useCoachChat
        console.log(`Loaded ${convertedMessages.length} messages from backend for thread ${threadId}`);
        
        // TODO: We should call a function from useCoachChat to update the messages
        // For now, the messages will be managed by the Jotai persistence
      } else {
        console.log(`No chat history found on backend for thread ${threadId}`);
      }

      // Mark this session as fetched
      setFetchedSessions(prev => new Set(prev).add(threadId));
      
    } catch (error) {
      console.error(`Failed to fetch backend chat history for thread ${threadId}:`, error);
      // Continue with local storage if backend fails
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Get current thread session for display
  const currentThreadSession = threadSessions.find(session => session.id === activeThreadId);

  const handleNewChat = () => {
    if (!user?.id) {return;}
    
    // Create new thread in Jotai state (automatically persisted)
    createNewThread();
    
    // The thread is automatically set as active by createNewThread()
    // Clear any local state
    clearMessages();
  };

  const handleSessionSelect = (sessionId: string) => {
    // Switch to the selected thread (Jotai will handle persistence)
    switchThread(sessionId);
    
    // Load history for this thread
    loadHistory();
    
    // Fetch fresh history from backend for this session
    fetchChatHistoryFromBackend(sessionId);
  };

  const handleDeleteSession = (sessionId: string) => {
    // Remove from fetched sessions
    setFetchedSessions(prev => {
      const newSet = new Set(prev);
      newSet.delete(sessionId);
      return newSet;
    });
    
    // Remove thread from Jotai state (automatically handles persistence)
    removeThread(sessionId);
    
    // If there are remaining threads, switch to the first one
    const remainingThreads = threadList.filter(id => id !== sessionId);
    if (remainingThreads.length > 0 && activeThreadId === sessionId) {
      switchThread(remainingThreads[0]);
      fetchChatHistoryFromBackend(remainingThreads[0]);
    }
  };

  const handleRenameSession = (sessionId: string, newTitle: string) => {
    // For now, we'll store the title as a special message in the thread
    // In a more sophisticated implementation, we could add a title field to ThreadState
    console.log(`Renaming session ${sessionId} to: ${newTitle}`);
    // TODO: Implement title storage in ThreadState if needed
  };


  const handleSendMessage = async (content: string) => {
    if (!user?.id) {
      return;
    }

    try {
      await sendMessage(content, user.id);
      
      // Mark this session as needing fresh fetch next time
      if (currentThreadId) {
        setFetchedSessions(prev => {
          const newSet = new Set(prev);
          newSet.delete(currentThreadId);
          return newSet;
        });
      }
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };


  return (
    <div className="flex h-full bg-bg-primary relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-30 pointer-events-none bg-pattern-subtle" />
      <div className="relative z-10 flex w-full h-full">
        <ChatSidebar
          sessions={threadSessions as any}
          activeSessionId={activeThreadId}
          onSessionSelect={handleSessionSelect}
          onNewChat={handleNewChat}
          onDeleteSession={handleDeleteSession}
          onRenameSession={handleRenameSession}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          isLoadingHistory={isLoadingHistory}
        />
        
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {currentThreadSession ? (
          <>
            {/* Chat Header */}
            <div className="bg-bg-secondary shadow-sm flex-shrink-0 w-full border-b border-border-light">
                <AppHeader
                  variant="minimal"
                  size="md"
                  hasBorder={false}
                  hasShadow={false}
                  leftContent={
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-8 h-8 bg-gradient-to-br from-primary-stop-1 to-primary-stop-2 rounded-lg flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h2 className="text-lg font-semibold text-text-primary truncate">
                          {currentThreadSession.title}
                        </h2>
                        <p className="text-xs text-text-muted">
                          {currentThreadSession.messages.length} message{currentThreadSession.messages.length !== 1 ? 's' : ''} • 
                          Last updated {currentThreadSession.updatedAt.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  }
                  rightContent={
                    <Button
                      onClick={() => setShowToolBlocks(!showToolBlocks)}
                      variant={showToolBlocks ? "secondary" : "neutral"}
                      size="small"
                      title={showToolBlocks ? "Hide tool blocks" : "Show tool blocks"}
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {showToolBlocks ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          )}
                        </svg>
                      }
                      iconPosition="left"
                    >
                      Tools
                    </Button>
                  }
                />
            </div>
            
            {/* Chat Interface */}
            <div className="flex-1 bg-bg-primary overflow-hidden">
              <ChatInterface
                messages={messages}
                streamingMessage={streamingMessage}
                onSendMessage={handleSendMessage}
                isLoading={isLoading}
                error={error}
                userId={user?.id}
                sessionId={currentThreadSession?.id}
                showToolBlocks={showToolBlocks}
                scrollPosition={scrollPosition}
                onScrollPositionChange={saveScrollPosition}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-bg-secondary">
            <div className="text-center max-w-md mx-auto px-6">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary-stop-1 to-primary-stop-2 flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-text-primary mb-4">Welcome to Treel Coach</h2>
              <p className="text-text-secondary mb-8 leading-relaxed">
                Your personal AI coach is here to help you achieve your health and wellness goals. 
                Start a conversation to begin your journey.
              </p>
              <Button
                onClick={handleNewChat}
                variant="primary"
                size="large"
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                }
                iconPosition="left"
              >
                Start New Chat
              </Button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}